import { ArrowLeft, ArrowRight } from '@untitledui/icons';
import { useEffect, useRef, useState } from 'react';

import { useBreakpoint } from '@/hooks/use-breakpoint';
import { Button } from '@/lib/base/buttons/button';
import { Pagination as PaginationBase } from '@/lib/base/pagination';
import { cx } from '@/utils/cx';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

interface PaginationProps {
  total: number;
  pageSize: number;
  page: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  className?: string;
}

function PaginationItem({
  value,
  isCurrent,
}: {
  value: number;
  isCurrent: boolean;
}) {
  return (
    <PaginationBase.Item
      value={value}
      isCurrent={isCurrent}
      className={({ isSelected }) =>
        cx(
          'flex size-10 cursor-pointer items-center justify-center p-3 text-sm font-medium text-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-secondary focus-visible:z-10 focus-visible:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2 rounded-lg',
          isSelected && 'bg-primary_hover text-secondary'
        )
      }
    >
      {value}
    </PaginationBase.Item>
  );
}

export default function Pagination({
  total,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  className = '',
}: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  const isDesktop = useBreakpoint('md');
  const [announcement, setAnnouncement] = useState('');
  const announcementTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
      }
    };
  }, []);

  // Suppress rendering when no controls are visible
  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }
  if (totalPages <= 1 && !isDesktop) {
    return null;
  } // Page size selector hidden on mobile

  return (
    <PaginationBase.Root
      page={page}
      total={totalPages}
      onPageChange={onPageChange}
      className={cx(
        'flex w-full items-center pt-3 pb-[env(safe-area-inset-bottom)] md:pt-5',
        className
      )}
    >
      {/* Left spacer — matches right column width for centering */}
      <div className='hidden md:block flex-1' />

      {/* Center: Previous + page numbers + Next grouped together */}
      {totalPages > 1 && (
        <>
          {/* Desktop: Previous + pages + Next inline */}
          <div className='hidden md:flex items-center gap-3'>
            <PaginationBase.PrevTrigger asChild>
              <Button iconLeading={ArrowLeft} color='link-gray' size='sm'>
                Previous
              </Button>
            </PaginationBase.PrevTrigger>

            <PaginationBase.Context>
              {({ pages }) => (
                <div className='flex items-center gap-0.5'>
                  {pages.map(p =>
                    p.type === 'page' ? (
                      <PaginationItem key={p.value} {...p} />
                    ) : (
                      <PaginationBase.Ellipsis
                        key={p.key}
                        className='flex size-10 shrink-0 items-center justify-center text-tertiary'
                      >
                        &#8230;
                      </PaginationBase.Ellipsis>
                    )
                  )}
                </div>
              )}
            </PaginationBase.Context>

            <PaginationBase.NextTrigger asChild>
              <Button iconTrailing={ArrowRight} color='link-gray' size='sm'>
                Next
              </Button>
            </PaginationBase.NextTrigger>
          </div>

          {/* Mobile: icons only for prev/next, no text */}
          <div className='flex flex-1 items-center justify-between md:hidden'>
            <PaginationBase.PrevTrigger asChild>
              <Button
                iconLeading={ArrowLeft}
                color='secondary'
                size='sm'
                aria-label='Previous page'
              />
            </PaginationBase.PrevTrigger>

            <PaginationBase.Context>
              {({ currentPage, total: totalPgs }) => (
                <span className='text-sm whitespace-pre text-fg-secondary'>
                  Page <span className='font-medium'>{currentPage}</span> of{' '}
                  <span className='font-medium'>{totalPgs}</span>
                </span>
              )}
            </PaginationBase.Context>

            <PaginationBase.NextTrigger asChild>
              <Button
                iconTrailing={ArrowRight}
                color='secondary'
                size='sm'
                aria-label='Next page'
              />
            </PaginationBase.NextTrigger>
          </div>
        </>
      )}

      {/* Right: Rows per page selector */}
      {/* Note: Page size selector hidden on mobile to save space - users can still navigate pages */}
      {onPageSizeChange ? (
        <div
          className='hidden md:flex flex-1 items-center justify-end gap-2 text-sm text-fg-secondary whitespace-nowrap'
          title='Page size selector available on larger screens'
        >
          <label htmlFor='page-size-select'>Rows per page:</label>
          <select
            id='page-size-select'
            value={String(pageSize)}
            onChange={e => {
              const newSize = Number(e.target.value);
              if (announcementTimeoutRef.current) {
                clearTimeout(announcementTimeoutRef.current);
              }
              setAnnouncement(
                `Page size changed to ${newSize}. Showing page 1.`
              );
              announcementTimeoutRef.current = setTimeout(
                () => setAnnouncement(''),
                3000
              );
              onPageSizeChange(newSize);
            }}
            className='rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer'
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={String(size)}>
                {size}
              </option>
            ))}
          </select>
          <span className='sr-only' aria-live='polite' aria-atomic='true'>
            {announcement}
          </span>
        </div>
      ) : (
        <div className='hidden md:block flex-1' />
      )}
    </PaginationBase.Root>
  );
}
